import { FormikProvider, FieldArray, useFormik } from 'formik'
import * as yup from 'yup'
import { DateTime } from 'luxon'
import { useState } from 'react'

import {
  Dialog,
  Form,
  Text,
  Alert,
  Card,
  Button,
  DatePicker,
  Separator,
  CardColors,
} from '@/ui'

import {
  adminUpdateUserTaxEstimate,
  notifyUserTaxEstimates,
  UserTaxEstimate,
} from '@/features/taxes/quarterly/userTaxEstimates.slice'

import { useAppDispatch } from '@/utils/typeHelpers'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { TypographyStyles, TextColors } from '@/styles'

const estimateSchema = yup.object().shape({
  estimateInCents: yup.number().nullable(),
  minimumPaymentInCents: yup.number().nullable(),
  amountPaidInCents: yup.number().nullable(),
  paidAt: yup.string().nullable(),
  noTaxPaymentNeeded: yup.boolean().nullable(),
})

const validationSchema = yup.object().shape({
  estimates: yup.array().of(estimateSchema),
})

interface UserTaxEstimateUpdateDialogProps {
  open: boolean
  close: () => void
  estimates: UserTaxEstimate[]
  taxQuarter: string
  userId: number
  homeState?: string | null
}

export const UserTaxEstimateUpdateDialog = ({
  open,
  close,
  estimates,
  taxQuarter,
  userId,
  homeState,
}: UserTaxEstimateUpdateDialogProps) => {
  const dispatch = useAppDispatch()
  const [notifying, setNotifying] = useState(false)
  const [notified, setNotified] = useState(false)

  const formik = useFormik({
    initialValues: { estimates },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setStatus }) => {
      for (const est of values.estimates) {
        await adminUpdateUserTaxEstimate(est.id, est)(dispatch)
      }
      setSubmitting(false)
      setStatus({ updated: true })
    },
  })

  const { values, setFieldValue, isSubmitting, status, handleSubmit } = formik

  const notifyUser = async () => {
    setNotifying(true)
    const res = await notifyUserTaxEstimates(userId, taxQuarter)(dispatch)
    setNotifying(false)
    setNotified(Boolean(res?.status))
  }

  /*
    Should be disabled if admin has clicked "notify" already
  */
  const checkNotifyDisabled = () => {
    let errorCount = 0

    for (const estimate of values.estimates) {
      /* check if we have estimate for their federal taxes */
      if (
        estimate.type === 'federal' &&
        !estimate.estimateInCents &&
        !estimate.noTaxPaymentNeeded
      ) {
        errorCount += 1
      } else if (estimate.type === 'state') {
        /* for state estimates, check if we have an estimate for their home state*/
        if (
          !estimate.noTaxPaymentNeeded &&
          estimate.filingState !== homeState
        ) {
          errorCount += 1
        }
      }
    }

    return errorCount > 0
  }

  const [taxYear, taxQuarterNum] = taxQuarter.split('-')
  return (
    <Dialog open={open} onClose={close}>
      <Dialog.Content size="xl">
        <FormikProvider value={formik}>
          <form onSubmit={handleSubmit}>
            <Text variant={TypographyStyles.H3}>
              {`Update Quarterly Tax Estimates for: Q${taxQuarterNum}, ${taxYear}`}
            </Text>

            {status?.updated && (
              <Alert variant="success">Estimates updated!</Alert>
            )}
            {notified && (
              <Alert variant="success">
                Successfully notified user! Notified State:{' '}
                {notified.toString()}
              </Alert>
            )}

            <Alert variant="error" title="To Update Tax estimates:">
              <p>1. Update all relevant tax estimates.</p>
              <p>2. Click &quot;Notify&quot; to notify the user.</p>
            </Alert>

            <FieldArray name="estimates">
              {() => (
                <div className="flex flex-col gap-4">
                  {values.estimates.map((estimate, idx) => (
                    <Card
                      key={estimate.id}
                      color={CardColors.LEVEL_1}
                      className="p-4"
                    >
                      <Text
                        variant={TypographyStyles.H2}
                        className="capitalize"
                      >
                        {estimate.type}
                        {estimate.type === 'state' && (
                          <span> — {estimate.filingState}</span>
                        )}
                      </Text>

                      <Text
                        variant={TypographyStyles.BODY_SM}
                        color={TextColors.MUTED}
                      >
                        Notified On:{' '}
                        {estimate.notifiedAt
                          ? DateTime.fromISO(estimate.notifiedAt).toFormat(
                              DATE_FORMATS_LUXON.DISPLAY_SHORT
                            )
                          : 'N/A'}
                      </Text>

                      <Separator />

                      {/* NO TAX PAYMENT NEEDED */}
                      <Form.Checkbox
                        name={`estimates[${idx}].noTaxPaymentNeeded`}
                        label="Mark No Tax Payment Needed"
                        className="bg-neutral-0"
                        onCheckedChange={(val: boolean) => {
                          setFieldValue(
                            `estimates[${idx}].noTaxPaymentNeeded`,
                            val
                          )
                          if (val) {
                            setFieldValue(
                              `estimates[${idx}].estimateInCents`,
                              0
                            )
                          }
                        }}
                      />

                      <br />

                      {!estimate.noTaxPaymentNeeded && (
                        <>
                          <Text
                            variant={TypographyStyles.EYEBROW}
                            className="mt-2"
                          >
                            Input Quarterly Tax Estimate:
                          </Text>

                          <Form.Input
                            name={`estimates[${idx}].estimateInCents`}
                            componentType="currencyInCents"
                            prefix="$"
                            inputClassName="bg-neutral-0"
                          />
                        </>
                      )}

                      {/* MINIMUM TAX PAYMENT */}
                      <Text variant={TypographyStyles.EYEBROW} className="mt-2">
                        Input Minimum Tax Payment:
                      </Text>

                      <Form.Input
                        name={`estimates[${idx}].minimumPaymentInCents`}
                        componentType="currencyInCents"
                        prefix="$"
                        inputClassName="bg-neutral-0"
                      />

                      {/* AMOUNT PAID */}
                      <Text variant={TypographyStyles.EYEBROW} className="mt-2">
                        Quarterly Tax Estimate Paid:
                      </Text>

                      <Form.Input
                        name={`estimates[${idx}].amountPaidInCents`}
                        componentType="currencyInCents"
                        prefix="$"
                        inputClassName="bg-neutral-0"
                      />

                      {/* PAID AT DATE */}
                      <Text variant={TypographyStyles.EYEBROW} className="mt-2">
                        Quarterly Tax Estimate Paid At:
                      </Text>

                      <DatePicker
                        value={estimate.paidAt || ''}
                        maxDate={new Date()}
                        inputClassName="bg-neutral-0"
                        onChange={(value) =>
                          setFieldValue(`estimates[${idx}].paidAt`, value)
                        }
                      />
                    </Card>
                  ))}
                </div>
              )}
            </FieldArray>

            <Dialog.Footer>
              <Button variant="secondary" onClick={close}>
                Close
              </Button>

              <Button
                variant="secondary"
                type="button"
                loading={notifying}
                disabled={checkNotifyDisabled()}
                onClick={() => {
                  notifyUser()
                }}
              >
                Notify User Estimates Ready
              </Button>

              <Button variant="primary" type="submit" loading={isSubmitting}>
                Update Estimates
              </Button>
            </Dialog.Footer>
          </form>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}
